package org.example.project.firebase

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.firestore
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flow
import org.example.project.model.AttackCharges
import org.example.project.model.AttackType
import org.example.project.model.MultiplayerGame
import org.example.project.model.SerializedShip
import org.example.project.game.AttackEngine
import org.example.project.game.ScoringEngine
import org.example.project.model.TimerMode
import org.example.project.util.currentTimeMillis

object GameManager {

    private val db = Firebase.firestore
    private const val GAMES = "games"

    /** Poll-based sync — avoids Firestore snapshot listener permission edge cases. */
    fun observeGame(gameId: String): Flow<MultiplayerGame?> = flow {

        while (true) {

            val game = runCatching {
                getGame(gameId)
            }.getOrNull()

            if (
                game != null &&
                game.status == "SHIP_PLACEMENT" &&
                game.placementSecondsRemaining() <= 0
            ) {

                val myUid = AuthManager.currentUserId()

                // Only Player 1 performs the timeout update
                if (myUid == game.player1Uid) {
                    handlePlacementTimeout(game.gameId)
                }
            }

            emit(game)

            if (game?.status == "FINISHED")
                break

            delay(1200)
        }
    }

    suspend fun getGame(gameId: String): MultiplayerGame? {
        val uid = AuthManager.currentUserId()
        println("getGame called - currentUserId: $uid")
        if (uid == null || uid.isEmpty()) {
            println("ERROR: User not authenticated when calling getGame")
            return null
        }
        
        // Refresh auth token before Firestore operation
        val tokenRefreshed = AuthManager.refreshAuthToken()
        println("Token refresh result: $tokenRefreshed")
        
        try {
            val snapshot = db.collection(GAMES).document(gameId).get()
            println("getGame snapshot exists: ${snapshot.exists}")
            return if (snapshot.exists) snapshot.data<MultiplayerGame>() else null
        } catch (e: Exception) {
            println("ERROR in getGame: ${e.message}")
            e.printStackTrace()
            return null
        }
    }

    suspend fun updatePlayerTimerMode(
        gameId: String,
        uid: String,
        mode: TimerMode
    ) {
        val game = getGame(gameId) ?: return

        val updates = mutableMapOf<String, Any>()

        if (game.player1Uid == uid) {
            updates["player1TimerMode"] = mode
            if(mode == TimerMode.TURN){
                updates["player1TurnTimerStart"] = currentTimeMillis()
            }
            else {
                updates["player1QuestionStart"] = currentTimeMillis()
            }
        } else {
            updates["player2TimerMode"] = mode
            if(mode == TimerMode.TURN){
                updates["player2TurnTimerStart"] = currentTimeMillis()
            }
            else {
                updates["player2QuestionStart"] = currentTimeMillis()
            }
        }

        Firebase.firestore
            .collection("games")
            .document(gameId)
            .update(updates)
    }

    suspend fun submitFleet(
        gameId: String,
        uid: String,
        ships: List<SerializedShip>
    ) {
        AuthManager.waitForUserId() ?: return
        val game = getGame(gameId) ?: return
        val isPlayer1 = game.isPlayer1(uid)

        if (isPlayer1) {
            db.collection(GAMES).document(gameId).set(
                mapOf(
                    "player1Ships" to ships,
                    "player1Ready" to true
                ),
                merge = true
            )
        } else {
            db.collection(GAMES).document(gameId).set(
                mapOf(
                    "player2Ships" to ships,
                    "player2Ready" to true
                ),
                merge = true
            )
        }

        // Wait a moment for Firestore to propagate, then check if both are ready
        delay(500)
        
        val updated = getGame(gameId) ?: return
        if (updated.bothReady() && updated.status == "SHIP_PLACEMENT") {
            db.collection(GAMES).document(gameId).set(
                mapOf(
                    "status" to "COUNTDOWN",
                    "countdownStart" to currentTimeMillis(),
                    "lastEvent" to "Both fleets deployed — battle commencing!"
                ),
                merge = true
            )
        }
    }

    suspend fun startBattle(gameId: String) {
        val game = getGame(gameId) ?: return
        if (game.status == "COUNTDOWN") {
            db.collection(GAMES).document(gameId).set(
                mapOf(
                    "status" to "BATTLE",
                    "currentTurnUid" to game.player1Uid,
                    "turnStartTime" to currentTimeMillis(),
                    "lastEvent" to "${game.player1Name} opens fire!"
                ),
                merge = true
            )
        }
    }

    suspend fun executeAttack(
        gameId: String,
        attackerUid: String,
        attackType: AttackType,
        row: Int,
        col: Int,
        lineHorizontal: Boolean
    ): MultiplayerGame? {
        val game = getGame(gameId) ?: return null
        if (!game.isMyTurn(attackerUid)) return game

        val isPlayer1 = game.isPlayer1(attackerUid)
        val opponentShips = game.opponentShips(attackerUid)
        val currentAttacks = game.attacksOnOpponent(attackerUid).let {
            if (it.size == 100) it else List(100) { "UNKNOWN" }
        }

        val charges = game.myCharges(attackerUid)
        if (!charges.canUse(attackType)) return game

        val (newAttackGrid, impactScore) = AttackEngine.applyAttack(
            currentAttacks,
            opponentShips,
            attackType,
            row,
            col,
            lineHorizontal
        )

        var score = game.myScore(attackerUid) +
            ScoringEngine.correctAnswerScore(0) +
            impactScore * 10

        var newCharges = charges.use(attackType)
        val opponentUid = game.opponentUid(attackerUid)

        val sunkBefore = opponentShips.count { ship ->
            AttackEngine.isShipSunk(currentAttacks, ship.toPlacedShip())
        }
        val sunkAfter = opponentShips.count { ship ->
            AttackEngine.isShipSunk(newAttackGrid, ship.toPlacedShip())
        }
        val newSinks = sunkAfter - sunkBefore

        if (newSinks > 0) {
            score += newSinks * ScoringEngine.shipSunkScore(0)
            repeat(newSinks) { newCharges = newCharges.rechargeOnSink() }
        }

        val won = AttackEngine.allShipsDestroyed(newAttackGrid, opponentShips)
        if (won) {
            score += ScoringEngine.winScore(0)
        }

        val attackerName = game.myName(attackerUid)
        val event = buildAttackEvent(attackerName, attackType, won)

        val updates = mutableMapOf<String, Any>(
            "lastEvent" to event
        )

        if (isPlayer1) {
            updates["attacksOnPlayer2"] = newAttackGrid
            updates["player1Score"] = score
            updates["player1Correct"] = game.player1Correct + 1
            updates["player1Charges"] = chargesMap(newCharges)
        } else {
            updates["attacksOnPlayer1"] = newAttackGrid
            updates["player2Score"] = score
            updates["player2Correct"] = game.player2Correct + 1
            updates["player2Charges"] = chargesMap(newCharges)
        }

        if (won) {
            updates["status"] = "FINISHED"
            updates["winnerUid"] = attackerUid
            updates["currentTurnUid"] = ""
        } else {
            updates["currentTurnUid"] = opponentUid
            updates["turnStartTime"] = currentTimeMillis()
        }

        db.collection(GAMES).document(gameId).set(updates, merge = true)

        return getGame(gameId)
    }

    suspend fun forfeit(
        gameId: String,
        loserUid: String
    ) {

        val game = getGame(gameId) ?: return

        val winner =
            if (game.player1Uid == loserUid)
                game.player2Uid
            else
                game.player1Uid

        db.collection(GAMES)
            .document(gameId)
            .set(
                mapOf(
                    "status" to "FINISHED",
                    "winnerUid" to winner,
                    "lastEvent" to "Opponent surrendered."
                ),
                merge = true
            )
    }

    suspend fun handlePlacementTimeout(gameId: String) {

        val game = getGame(gameId) ?: return

        if (game.status == "FINISHED") return

        if (game.status != "SHIP_PLACEMENT") return


        if (!game.player1Ready && !game.player2Ready) {
            return
        }

        val loserUid =
            if (!game.player1Ready)
                game.player1Uid
            else
                game.player2Uid

        val winnerUid =
            if (loserUid == game.player1Uid)
                game.player2Uid
            else
                game.player1Uid

        db.collection(GAMES)
            .document(gameId)
            .set(
                mapOf(
                    "status" to "FINISHED",
                    "winnerUid" to winnerUid,
                    "lastEvent" to "Fleet deployment timed out."
                ),
                merge = true
            )
    }

    suspend fun recordWrongAnswer(
        gameId: String,
        uid: String
    ) {
        val game = getGame(gameId) ?: return
        if (!game.isMyTurn(uid)) return

        val isPlayer1 = game.isPlayer1(uid)
        val penalties = game.myPenalties(uid) + 1
        val opponentUid = game.opponentUid(uid)
        val name = game.myName(uid)

        val updates = mutableMapOf<String, Any>(
            "currentTurnUid" to opponentUid,
            "lastEvent" to "$name missed the intel check — turn forfeited!",
            "turnStartTime" to currentTimeMillis()
        )

        if (isPlayer1) {
            updates["player1Penalties"] = penalties
        } else {
            updates["player2Penalties"] = penalties
        }

        db.collection(GAMES).document(gameId).set(updates, merge = true)
    }

    suspend fun finalizeGameStats(game: MultiplayerGame, myUid: String) {
        if (game.winnerUid.isEmpty()) return

        val won = game.winnerUid == myUid
        val score = game.myScore(myUid)

        FirestoreManager.updateStats(
            uid = myUid,
            won = won,
            scoreDelta = if (won) score else score / 4,
            ratingDelta = if (won) 25 else -15
        )

        FirestoreManager.setInGame(myUid, false)
        db.collection("users")
            .document(myUid)
            .set(
                mapOf(
                    "currentGameId" to ""
                ),
                merge = true
            )
    }

    private fun chargesMap(charges: AttackCharges): Map<String, Int> = mapOf(
        "crossFire" to charges.crossFire,
        "clusterBomb" to charges.clusterBomb,
        "lineStrike" to charges.lineStrike,
        "sonar" to charges.sonar
    )

    private fun buildAttackEvent(
        attacker: String,
        type: AttackType,
        won: Boolean
    ): String {
        if (won) return "$attacker destroyed the enemy fleet — VICTORY!"
        return when (type) {
            AttackType.TORPEDO -> "$attacker launched a torpedo!"
            AttackType.CROSS_FIRE -> "$attacker unleashed cross fire!"
            AttackType.CLUSTER_BOMB -> "$attacker deployed a cluster bomb!"
            AttackType.LINE_STRIKE -> "$attacker fired a line strike!"
            AttackType.SONAR -> "$attacker pinged sonar — intel gathered!"
        }
    }
}
